import { Link } from 'react-router'

export default function Divisions() {
  const divisions = [
    {
      id: 1,
      name: 'Services',
      description: 'We deliver technology, operational solutions and training across multiple industries. Our division focuses on website design & development, cloud computing, data analytics, mobile app development, and custom software solutions.',
      image: '/assets/heroImages/appdevelopment.webp',
      color: 'from-emerald-500 to-green-600',
      hoverColor: 'hover:from-emerald-600 hover:to-green-700',
      status: 'active',
      hasServices: true
    },
    {
      id: 2,
      name: 'Transport',
      description: 'Revolutionary transport solutions designed to transform logistics and mobility. We are developing innovative technologies to enhance transportation efficiency and sustainability.',
      image: '/assets/heroImages/mainTransport.webp',
      color: 'from-blue-500 to-indigo-600',
      hoverColor: 'hover:from-blue-600 hover:to-indigo-700',
      status: 'coming-soon',
      hasServices: false
    },
    {
      id: 3,
      name: 'Agriculture',
      description: 'Advanced agricultural technologies to boost productivity and sustainability. Our solutions focus on smart farming, crop monitoring, and sustainable agricultural practices.',
      image: '/assets/heroImages/mainAgriculture.webp',
      color: 'from-green-500 to-emerald-600',
      hoverColor: 'hover:from-green-600 hover:to-emerald-700',
      status: 'coming-soon',
      hasServices: false
    },
    {
      id: 4,
      name: 'Energy',
      description: 'Clean energy solutions for a sustainable future. We develop renewable energy systems, energy storage solutions, and smart grid technologies.',
      image: '/assets/heroImages/mainenergy.webp',
      color: 'from-yellow-500 to-orange-600',
      hoverColor: 'hover:from-yellow-600 hover:to-orange-700',
      status: 'coming-soon',
      hasServices: false
    },
    {
      id: 5,
      name: 'Academy',
      description: 'Professional training and certification programs for digital transformation. We provide comprehensive education in technology, business, and innovation.',
      image: '/assets/heroImages/mainEducation.webp',
      color: 'from-purple-500 to-pink-600',
      hoverColor: 'hover:from-purple-600 hover:to-pink-700',
      status: 'coming-soon',
      hasServices: false
    }
  ]

  return (
    <div className='min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-20 relative overflow-hidden' id="divisions">
      {/* Background decorative elements */}
      <div className='absolute inset-0'>
        <div className='absolute top-20 left-20 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse'></div>
        <div className='absolute bottom-20 right-20 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-200'></div>
      </div>
      
      <div className='relative z-10 container mx-auto px-6'>
        <div className='text-center mb-16'>
          <h2 className='text-4xl md:text-6xl font-bold text-green-900 font-comfortaa mb-6'>
            Areas <span className='text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600'>of specialization</span>
          </h2>
          <div className='w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-600 mx-auto rounded-full mb-8'></div>
          <p className='text-lg md:text-xl text-gray-700 font-poppins max-w-3xl mx-auto leading-relaxed'>
            Explore our diverse portfolio of <span className='font-semibold text-green-800'>innovative divisions</span> featuring cutting-edge web development, mobile app development, cloud computing, and comprehensive technology solutions across multiple industries.
          </p>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto'>
          {divisions.map(division => (
            <DivisionCard key={division.id} division={division} />
          ))}
        </div>
      </div>
    </div>
  )
}

function DivisionCard({ division }) {
  return (
    <div className='group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden'>
      {/* Division Image */}
      <div className='relative h-48 overflow-hidden'>
          <img 
            src={division.image.replace(/\.(jpg|jpeg|png)$/i, '.webp')}
            alt={division.name}
            loading='lazy'
            className='w-full h-full object-cover transition-transform duration-500 group-hover:scale-110'
            onError={e => { e.target.src = division.image; }}
          />
        {/* Image overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t ${division.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
        
        {/* Status Badge */}
        {division.status === 'coming-soon' && (
          <div className='absolute top-4 right-4'>
            <span className='inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold'>
              Coming Soon
            </span>
          </div>
        )}
        
        {/* Division title overlay */}
        <div className='absolute bottom-4 left-4 right-4'>
          <h3 className='text-xl font-bold text-white font-poppins drop-shadow-lg'>
            {division.name}
          </h3>
        </div>
      </div>
      
      <div className='relative p-6'>            
        {/* Division description */}
        <p className='text-gray-600 leading-relaxed font-poppins mb-6 text-sm'>
          {division.description}
        </p>
        
        {/* Action Buttons */}
        <div className='flex flex-col gap-3'>
          {division.hasServices ? (
            <>
              <Link 
                to='/request-quote'
                className={`inline-flex items-center justify-center bg-gradient-to-r ${division.color} ${division.hoverColor} text-white font-semibold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg gap-2`}
              >
                Get Started
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
                </svg>
              </Link>
              <Link 
                to='/services'
                className='inline-flex items-center justify-center border-2 border-gray-200 hover:border-green-500 text-gray-700 hover:text-green-600 font-semibold py-3 px-6 rounded-full transition-all duration-300 gap-2'
              >
                Learn More
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </Link>
            </>
          ) : (
            <div className='text-center'>
              <button 
                disabled
                className={`inline-flex items-center justify-center bg-gradient-to-r ${division.color} opacity-60 cursor-not-allowed text-white font-semibold py-3 px-6 rounded-full gap-2 w-full`}
              >
                Coming Soon
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className='absolute top-4 left-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full opacity-80'></div>
    </div>
  )
}